import { useState } from 'react';
import {
  BookOpen,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Compass,
  Loader2,
  Wand2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatPipelineJson } from '@/lib/scriptAudioAlignPipeline';
import {
  COURSE_NARRATION_LANGUAGES,
  COURSE_NARRATION_TONES,
  courseStepLabel,
} from '@/lib/scriptAudioAlignCourse';
import { artifactLabel } from '@/lib/scriptAudioAlignArtifacts';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const STAGE_ICONS = {
  analyze_domain: Compass,
  plan_curriculum: BookOpen,
};

function StageStatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
  if (status === 'failed') return <XCircle className="w-4 h-4 text-red-400" />;
  if (status === 'running') return <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />;
  return <span className="w-3.5 h-3.5 rounded-full border border-slate-500" />;
}

function StageCard({ stepId, status, children, rawOutput }) {
  const [showRaw, setShowRaw] = useState(false);
  const Icon = STAGE_ICONS[stepId] || TwinneeLogo;
  return (
    <section className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--glass-border)' }}>
      <header className="flex items-center gap-2 px-4 py-3 bg-slate-900/30 border-b border-white/5">
        <Icon className="w-4 h-4 text-violet-400 shrink-0" />
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex-1">{courseStepLabel({ step_id: stepId })}</h3>
        <StageStatusIcon status={status} />
        {rawOutput ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? 'Hide JSON' : 'JSON'}
          </Button>
        ) : null}
      </header>
      <div className="p-4 space-y-3">
        {children}
        {showRaw && rawOutput ? (
          <pre className="text-[11px] text-slate-300 whitespace-pre-wrap overflow-x-auto p-3 max-h-64 overflow-y-auto font-mono rounded-lg border border-slate-700/60 bg-slate-950/60">
            {formatPipelineJson(rawOutput)}
          </pre>
        ) : null}
      </div>
    </section>
  );
}

function Chips({ items, tone = 'slate' }) {
  if (!items?.length) return null;
  const toneClass = tone === 'violet'
    ? 'bg-violet-500/15 text-violet-200 border-violet-500/30'
    : 'bg-slate-700/40 text-slate-200 border-slate-600/40';
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={`${it}-${i}`} className={cn('text-[11px] px-2 py-0.5 rounded-full border', toneClass)}>
          {it}
        </span>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      {children}
    </div>
  );
}

function stepStatus(steps, id) {
  return steps.find((s) => s.step_id === id)?.status;
}

function stepOutput(steps, id) {
  return steps.find((s) => s.step_id === id)?.output || null;
}

function DomainStage({ domain, status, raw }) {
  return (
    <StageCard stepId="analyze_domain" status={status} rawOutput={raw}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Difficulty">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="capitalize">{domain?.difficulty || '—'}</Badge>
            {domain?.difficulty_reason ? (
              <span className="text-xs text-slate-400">{domain.difficulty_reason}</span>
            ) : null}
          </div>
        </Field>
        <Field label="Knowledge types">
          <Chips items={domain?.knowledge_types} />
        </Field>
      </div>
      <Field label="Concepts">
        <ul className="space-y-1">
          {(domain?.concepts || []).map((c, i) => (
            <li key={`${c.name}-${i}`} className="text-sm text-[var(--text-primary)]">
              <span className="font-medium">{c.name}</span>
              {c.summary ? <span className="text-slate-400"> — {c.summary}</span> : null}
            </li>
          ))}
        </ul>
      </Field>
      {domain?.dependencies?.length ? (
        <Field label="Dependencies">
          <Chips items={domain.dependencies.map((d) => `${d.from_concept} → ${d.to_concept}`)} />
        </Field>
      ) : null}
      <Field label="Learning goals">
        <ul className="list-disc pl-5 space-y-0.5">
          {(domain?.learning_goals || []).map((g, i) => (
            <li key={i} className="text-sm text-slate-300">{g}</li>
          ))}
        </ul>
      </Field>
    </StageCard>
  );
}

function ArtifactChips({ artifacts }) {
  if (!artifacts?.length) return null;
  const sorted = [...artifacts].sort((a, b) => (a.priority || 999) - (b.priority || 999));
  return (
    <ul className="space-y-1.5">
      {sorted.map((a, i) => (
        <li
          key={`${a.artifact_type}-${i}`}
          className="flex items-start gap-2 text-xs rounded-lg border border-violet-500/20 bg-violet-500/5 px-2 py-1.5"
          title={a.rationale || undefined}
        >
          <span className="shrink-0 w-6 h-6 rounded bg-violet-500/20 text-violet-200 text-[10px] font-semibold flex items-center justify-center">
            P{a.priority || i + 1}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <Boxes className="w-3 h-3 text-violet-300" />
              <span className="font-medium text-violet-100">{a.heading || artifactLabel(a.artifact_type)}</span>
              <span className="font-mono text-[10px] text-slate-500">{a.artifact_type}</span>
            </div>
            {a.rationale ? <p className="text-[11px] text-slate-400 mt-0.5">{a.rationale}</p> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function CurriculumStage({ curriculum, status, raw }) {
  return (
    <StageCard stepId="plan_curriculum" status={status} rawOutput={raw}>
      <div className="space-y-3">
        {(curriculum?.topics || []).map((t, i) => (
          <div key={`${t.title}-${i}`} className="rounded-lg border border-slate-700/50 p-3 space-y-2">
            <p className="text-sm font-semibold text-[var(--text-primary)]">{i + 1}. {t.title}</p>
            {t.learning_objectives?.length ? (
              <Field label="Objectives">
                <ul className="list-disc pl-5 space-y-0.5">
                  {t.learning_objectives.map((o, j) => <li key={j} className="text-xs text-slate-300">{o}</li>)}
                </ul>
              </Field>
            ) : null}
            {t.prerequisites?.length ? <Field label="Prerequisites"><Chips items={t.prerequisites} /></Field> : null}
            {t.supporting_topics?.length ? <Field label="Supporting"><Chips items={t.supporting_topics} /></Field> : null}
            {t.artifacts?.length ? (
              <Field label="Artifacts for this topic">
                <ArtifactChips artifacts={t.artifacts} />
              </Field>
            ) : null}
            {(t.subtopic_plans?.length || t.subtopics?.length) ? (
              <Field label="Subtopics & artifacts">
                <div className="space-y-2">
                  {(t.subtopic_plans?.length
                    ? t.subtopic_plans
                    : (t.subtopics || []).map((title) => ({ title, artifacts: [] }))
                  ).map((sp, j) => (
                    <div key={`${sp.title}-${j}`} className="rounded-md border border-slate-800/80 bg-slate-950/40 p-2 space-y-1.5">
                      <p className="text-xs font-medium text-slate-200">{sp.title}</p>
                      {sp.artifacts?.length ? (
                        <ArtifactChips artifacts={sp.artifacts} />
                      ) : (
                        <p className="text-[11px] text-slate-500">No artifacts suggested</p>
                      )}
                    </div>
                  ))}
                </div>
              </Field>
            ) : null}
          </div>
        ))}
      </div>
      {curriculum?.roadmap?.length ? (
        <Field label="Roadmap">
          <ol className="space-y-1">
            {curriculum.roadmap.map((r, i) => (
              <li key={i} className="text-xs text-slate-300">
                <span className="text-violet-300 font-mono mr-1">{r.order}.</span>
                {r.title}{r.detail ? <span className="text-slate-500"> — {r.detail}</span> : null}
              </li>
            ))}
          </ol>
        </Field>
      ) : null}
    </StageCard>
  );
}

function RawPipeline({ steps }) {
  const [open, setOpen] = useState(false);
  if (!steps.length) return null;
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--glass-border)' }}>
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-800/30"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pipeline steps (raw input/output)</span>
      </button>
      {open ? (
        <ul className="p-3 space-y-2">
          {steps.map((step) => (
            <li key={step.step_id} className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <StageStatusIcon status={step.status} />
                <span className="text-sm font-medium text-slate-100">{courseStepLabel(step)}</span>
                <span className="text-[10px] font-mono text-violet-300/80">{step.module}</span>
              </div>
              {step.input && Object.keys(step.input).length ? (
                <pre className="text-[11px] text-slate-400 whitespace-pre-wrap overflow-x-auto p-2 max-h-40 overflow-y-auto font-mono rounded border border-slate-700/50 bg-slate-950/60">
                  {formatPipelineJson(step.input)}
                </pre>
              ) : null}
              {step.output && Object.keys(step.output).length ? (
                <pre className="text-[11px] text-emerald-200/80 whitespace-pre-wrap overflow-x-auto p-2 max-h-48 overflow-y-auto font-mono rounded border border-emerald-500/20 bg-emerald-950/20">
                  {formatPipelineJson(step.output)}
                </pre>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function CoursePlannerPanel({
  plan,
  loading = false,
  onStartExplain,
  explainLoading = false,
  narrationLanguage = 'en',
  narrationTone = 'serious',
  onNarrationLanguageChange,
  onNarrationToneChange,
}) {
  if (loading && !plan) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 text-sm gap-2">
        <Loader2 className="w-5 h-5 animate-spin" />
        Planning course…
      </div>
    );
  }
  if (!plan) return null;

  const steps = Array.isArray(plan.pipeline_steps) ? plan.pipeline_steps : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <TwinneeLogo className="w-4 h-4 text-violet-400" />
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Course plan</h2>
        </div>
        <Badge variant="outline" className="text-[10px]">
          {plan.used_llm ? 'AI-planned' : 'Heuristic plan'}
        </Badge>
      </div>

      <DomainStage domain={plan.domain_analysis} status={stepStatus(steps, 'analyze_domain')} raw={stepOutput(steps, 'analyze_domain')} />
      <CurriculumStage curriculum={plan.curriculum} status={stepStatus(steps, 'plan_curriculum')} raw={stepOutput(steps, 'plan_curriculum')} />

      {plan.curriculum?.topics?.length ? (
        <div className="rounded-2xl border p-4 space-y-4" style={{ borderColor: 'var(--glass-border)' }}>
          <p className="text-sm text-slate-300">
            Generate lesson artifacts for each topic and subtopic. Each artifact gets its own
            spoken narration (plus a short overview). ChatGPT maps those lines to on-screen cues.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Narration language</Label>
              <Select
                value={narrationLanguage}
                onValueChange={(v) => onNarrationLanguageChange?.(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  {COURSE_NARRATION_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Narration style</Label>
              <Select
                value={narrationTone}
                onValueChange={(v) => onNarrationToneChange?.(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tone" />
                </SelectTrigger>
                <SelectContent>
                  {COURSE_NARRATION_TONES.map((tone) => (
                    <SelectItem key={tone.value} value={tone.value}>
                      {tone.label}
                      {tone.hint ? ` — ${tone.hint}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => onStartExplain?.(plan, { language: narrationLanguage, tone: narrationTone })}
            disabled={explainLoading || loading}
            className="w-full sm:w-auto"
          >
            {explainLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4 mr-2" />
            )}
            {explainLoading ? 'Starting artifact queue…' : 'Generate lesson artifacts (topic + subtopic)'}
          </Button>
        </div>
      ) : null}

      <RawPipeline steps={steps} />
    </div>
  );
}
